import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { IMAGES } from '../mock';

const EASE = [0.22, 1, 0.36, 1];
const LETTERS = [0, 1, 2, 3, 4, 5];
const HOLD_MS = 3850;
const WING_CLIP = 'polygon(38% 56%, 83% 4%, 88% 0%, 99% 17%, 100% 35%, 95% 48%, 87% 58%, 71% 66%, 50% 66%)';
const maskStyle = {
  WebkitMaskImage: 'url(/intro/full.webp)',
  maskImage: 'url(/intro/full.webp)',
  WebkitMaskSize: '100% 100%',
  maskSize: '100% 100%',
};

const IntroLogo = () => (
  <div className="relative w-[82vw] max-w-[700px] aspect-[1833/771]" data-testid="intro-logo">
    {LETTERS.map((i) => (
      <motion.img
        key={i}
        src={`/intro/letter-${i}.webp`}
        alt=""
        draggable={false}
        className="absolute inset-0 w-full h-full select-none"
        initial={{ opacity: 0, y: 46, filter: 'blur(14px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        transition={{ duration: 0.95, delay: 0.25 + i * 0.085, ease: EASE }}
      />
    ))}
    <motion.img
      src="/intro/blue.webp"
      alt="Tropic Blue"
      draggable={false}
      className="absolute inset-0 w-full h-full select-none"
      initial={{ clipPath: 'inset(0% 100% 0% 0%)' }}
      animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
      transition={{ duration: 1.05, delay: 0.95, ease: [0.65, 0, 0.35, 1] }}
    />
    <motion.div
      className="absolute inset-0 pointer-events-none"
      style={{
        ...maskStyle,
        backgroundImage: 'linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.9) 50%, transparent 62%)',
        backgroundSize: '260% 100%',
      }}
      initial={{ backgroundPosition: '130% 0%' }}
      animate={{ backgroundPosition: '-30% 0%' }}
      transition={{ duration: 1.1, delay: 2.05, ease: 'easeInOut' }}
    />
  </div>
);

const Ripples = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
    {[0, 0.5, 1].map((d) => (
      <motion.span
        key={d}
        className="absolute rounded-full border border-sky-300/50"
        style={{ width: 180, height: 180 }}
        initial={{ scale: 0.2, opacity: 0 }}
        animate={{ scale: [0.2, 5.5], opacity: [0, 0.7, 0] }}
        transition={{ duration: 2.6, delay: 0.1 + d, ease: 'easeOut' }}
      />
    ))}
  </div>
);

const Hummingbird = () => (
  <motion.div
    data-testid="intro-hummingbird"
    className="absolute left-[52%] -top-[72px] sm:-top-[124px] w-[64px] sm:w-[104px] pointer-events-none z-10 drop-shadow-[0_10px_14px_rgba(15,44,107,0.18)]"
    initial={{ x: '-70vw', y: '22vh', rotate: 14, opacity: 0 }}
    animate={{
      x: ['-70vw', '-28vw', '0vw', '0vw', '9vw'],
      y: ['22vh', '-7vh', '0vh', '0vh', '-90vh'],
      rotate: [14, 6, 0, 0, -38],
      opacity: [0, 1, 1, 1, 1],
    }}
    transition={{ duration: 2.5, delay: 1.7, times: [0, 0.32, 0.6, 0.84, 1], ease: 'easeInOut' }}
  >
    <motion.div
      animate={{ y: [0, -5, 0] }}
      transition={{ duration: 0.55, repeat: Infinity, ease: 'easeInOut' }}
    >
      <div className="relative -scale-x-100">
        <img src="/intro/bird.webp" alt="" draggable={false} className="w-full select-none" />
        <motion.img
          src="/intro/bird.webp"
          alt=""
          draggable={false}
          className="absolute inset-0 w-full select-none opacity-55 blur-[0.6px]"
          style={{ clipPath: WING_CLIP, WebkitClipPath: WING_CLIP, transformOrigin: '43% 58%' }}
          animate={{ rotate: [-16, 12] }}
          transition={{ duration: 0.07, repeat: Infinity, repeatType: 'reverse', ease: 'linear' }}
        />
      </div>
    </motion.div>
  </motion.div>
);

const Tagline = () => (
  <div className="mt-10 flex flex-col items-center gap-5">
    <motion.div
      className="h-px w-40 bg-gradient-to-r from-transparent via-[#c4a24a] to-transparent origin-center"
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: 0.9, delay: 1.85, ease: EASE }}
    />
    <motion.p
      data-testid="intro-tagline"
      className="text-[10px] sm:text-[11px] tracking-[0.42em] sm:tracking-[0.5em] text-[#0f2c6b]/70 font-medium text-center pl-[0.5em]"
      initial={{ opacity: 0, y: 10, letterSpacing: '0.9em' }}
      animate={{ opacity: 1, y: 0, letterSpacing: '0.5em' }}
      transition={{ duration: 1.1, delay: 2.0, ease: EASE }}
    >
      PREMIUM PACKAGED DRINKING WATER
    </motion.p>
  </div>
);

const IntroSplash = ({ onDone }) => {
  useEffect(() => {
    [IMAGES.mountainBg, '/bottles/lineup.jpeg'].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    const t = setTimeout(onDone, HOLD_MS);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      data-testid="intro-splash"
      className="fixed inset-0 z-[200] bg-[#fafaf7]"
      initial={{ y: 0 }}
      exit={{ y: '-100%', transition: { duration: 1.15, ease: [0.76, 0, 0.24, 1] } }}
    >
      <div className="absolute left-0 right-0 top-full h-[14vh] bg-[#fafaf7] rounded-b-[50%] shadow-[0_30px_60px_-30px_rgba(15,44,107,0.25)]" />
      <div className="absolute inset-0 overflow-hidden bg-[radial-gradient(circle_at_center,rgba(186,224,255,0.32),rgba(186,224,255,0.08)_45%,transparent_75%)]">
        <Ripples />
      </div>
      <div className="relative h-full flex flex-col items-center justify-center">
        <div className="relative">
          <motion.div exit={{ opacity: 0, y: -60, scale: 0.96, transition: { duration: 0.7, ease: EASE } }}>
            <IntroLogo />
            <Tagline />
          </motion.div>
          <Hummingbird />
        </div>
      </div>
      <motion.div
        className="absolute bottom-0 left-0 h-[2px] bg-[#c4a24a] origin-left w-full"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: HOLD_MS / 1000, ease: 'linear' }}
      />
      <button
        data-testid="intro-skip-button"
        onClick={onDone}
        className="absolute bottom-6 right-6 sm:bottom-8 sm:right-10 text-[10px] tracking-[0.4em] font-medium text-[#0f2c6b]/50 hover:text-[#c4a24a] transition-colors duration-300"
      >
        SKIP
      </button>
    </motion.div>
  );
};

export default IntroSplash;
